import { authFetch } from './auth';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
    /** Délai demandé par l'API (en-tête Retry-After), en millisecondes, s'il est présent. */
    public retryAfterMs?: number,
  ) {
    super(message);
  }
}

/** Erreur réseau (API down, DNS, timeout, etc.) — distincte d'une erreur HTTP. */
export class NetworkError extends Error {
  constructor(message = "Impossible de joindre l'API. Vérifiez votre connexion ou réessayez.") {
    super(message);
    this.name = 'NetworkError';
  }
}

export const MSG_TOO_MANY = 'Trop de requêtes, réessayez dans un instant.';
export const MSG_UNAVAILABLE = 'Service momentanément indisponible, réessayez dans un instant.';

/** Libellés HTTP standard (anglais) que l'API renvoie dans `error` : jamais montrés tels quels. */
const STANDARD_REASONS = new Set([
  'Too Many Requests',
  'Internal Server Error',
  'Bad Gateway',
  'Service Unavailable',
  'Gateway Timeout',
]);

export function extractMessage(body: unknown, status: number): string {
  // 429 : le corps de @fastify/rate-limit est en anglais (« Rate limit exceeded… »).
  if (status === 429) return MSG_TOO_MANY;
  if (body && typeof body === 'object' && 'error' in body) {
    const e = (body as { error?: unknown }).error;
    if (typeof e === 'string' && e.trim().length > 0 && !STANDARD_REASONS.has(e.trim())) return e;
  }
  if (status === 401) return 'Session expirée — reconnectez-vous.';
  if (status === 403) return 'Action non autorisée pour votre profil.';
  if (status === 404) return 'Ressource introuvable.';
  if (status === 502 || status === 503 || status === 504) return MSG_UNAVAILABLE;
  if (status >= 500) return 'Erreur serveur — réessayez ou contactez le support.';
  return `Erreur HTTP ${status}`;
}

/**
 * En-tête Retry-After → millisecondes (secondes entières ou date HTTP).
 * `undefined` si absent ou illisible.
 */
export function parseRetryAfter(value: string | null, now: number = Date.now()): number | undefined {
  if (!value) return undefined;
  const v = value.trim();
  if (/^\d+$/.test(v)) return Number(v) * 1000;
  const at = Date.parse(v);
  if (Number.isNaN(at)) return undefined;
  return Math.max(0, at - now);
}

/**
 * Erreur passagère : la session n'est pas en cause, on réessaie plus tard sans
 * déconnecter (limite de débit, service indisponible, réseau).
 */
export function isTransientError(e: unknown): boolean {
  if (e instanceof NetworkError) return true;
  return e instanceof ApiError && (e.status === 429 || e.status >= 500);
}

/** Message à afficher pour une erreur passagère. */
export function transientMessage(e: unknown): string {
  if (e instanceof ApiError && e.status === 429) return MSG_TOO_MANY;
  if (e instanceof NetworkError) return e.message;
  return MSG_UNAVAILABLE;
}

const RETRY_DEFAULT_MS = 5_000;
const RETRY_MIN_MS = 1_000;
const RETRY_MAX_MS = 60_000;

/** Délai avant nouvel essai : Retry-After s'il est fourni, borné entre 1 s et 60 s. */
export function retryDelayMs(e: unknown): number {
  const asked = e instanceof ApiError ? e.retryAfterMs : undefined;
  const d = asked ?? RETRY_DEFAULT_MS;
  return Math.min(RETRY_MAX_MS, Math.max(RETRY_MIN_MS, d));
}

async function toApiError(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* ignore */
  }
  return new ApiError(res.status, extractMessage(body, res.status), body, parseRetryAfter(res.headers.get('retry-after')));
}

async function safeFetch(path: string, init: RequestInit): Promise<Response> {
  try {
    return await authFetch(path, init);
  } catch (e) {
    // fetch ne throw que sur erreur réseau (down, CORS, abort) — pas sur 4xx/5xx.
    if (e instanceof Error && e.name === 'AbortError') throw e;
    throw new NetworkError();
  }
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await safeFetch(path, { method: 'GET', signal });
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as T;
}

export async function apiPost<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return apiSend<T>('POST', path, body, signal);
}

export async function apiPatch<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return apiSend<T>('PATCH', path, body, signal);
}

async function apiSend<T>(method: 'POST' | 'PATCH', path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const res = await safeFetch(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
    signal,
  });
  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
